import axios from 'axios';
import { getToken, getNewToken } from './Auth.js';

const apiBaseUrl = process.env.REACT_APP_API_ENDPOINT;
const dplBaseUrl = process.env.REACT_APP_DPL_BASE_URL;

const getValidToken = async () => {
  let token = getToken();

  if (!token) {
    const refreshed = await getNewToken();

    if (!refreshed) {
      throw new Error('No valid token available');
    }

    token = getToken();
  }

  return token;
};

// Helper function for making API requests with token refresh capability
const makeAuthenticatedRequest = async (requestFn) => {
  const token = await getValidToken();

  try {
    return await requestFn(token);
  } catch (error) {
    if (error.response && error.response.status === 401) {
      const newToken = await getValidToken();
      return await requestFn(newToken);
    }

    throw error;
  }
};

const getUserDetail = async (testid = null) => {
  return makeAuthenticatedRequest(async (token) => {
    const result = await axios({
      method: 'GET',
      url: `${apiBaseUrl}/user-detail${testid ? '?testid=' + testid : ''}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return result.data;
  });
};

const getClassList = async (testid = null, strm = null) => {
  return makeAuthenticatedRequest(async (token) => {
    const result = await axios({
      method: 'GET',
      url: `${apiBaseUrl}/class-list${strm ? '?strm=' + strm : ''}${testid ? '&testid=' + testid : ''}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return result.data;
  });
};

const getEdnaAccess = async (testid = null) => {
  return makeAuthenticatedRequest(async (token) => {
    const result = await axios({
      method: 'GET',
      url: `${apiBaseUrl}/edna-access${testid ? '?testid=' + testid : ''}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return result.data;
  });
};

const getEdnaAccessList = async () => {
  return makeAuthenticatedRequest(async (token) => {
    const result = await axios({
      method: 'GET',
      url: `${apiBaseUrl}/edna-access-list?servicePath=Syllabusupload.Admin`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return result.data;
  });
};

const getDownloadUrl = async (classNumber, strm) => {
  // return makeAuthenticatedRequest(async (token) => {
  const result = await axios({
    method: 'GET',
    url: `${apiBaseUrl}/get-download-url?classNumber=${classNumber}&strm=${strm}`,
    // headers: {
    //   Authorization: `Bearer ${token}`,
    // },
  });
  return result.data;
  // });
};

const removeSyllabus = async (classNumber, strm) => {
  return makeAuthenticatedRequest(async (token) => {
    await axios({
      method: 'DELETE',
      url: `${apiBaseUrl}/remove-syllabi`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      data: { classNumber, strm },
    });
    return 'Syllabus removed successfully.';
  });
};

const addUser = async (user) => {
  return makeAuthenticatedRequest(async (token) => {
    const result = await axios({
      method: 'POST',
      url: `${apiBaseUrl}/manage-users`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      data: user,
    });
    return result.data;
  });
};

const removeUsers = async (userIds) => {
  return makeAuthenticatedRequest(async (token) => {
    const result = await axios({
      method: 'DELETE',
      url: `${apiBaseUrl}/manage-users`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      data: userIds,
    });
    return result.data;
  });
};

const getUsers = async () => {
  return makeAuthenticatedRequest(async (token) => {
    const result = await axios({
      method: 'GET',
      url: `${apiBaseUrl}/manage-users`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return result.data;
  });
};

const search = async (input, testid = null) => {
  return makeAuthenticatedRequest(async (token) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 7000);

    try {
      const result = await axios({
        method: 'POST',
        url: `${apiBaseUrl}/class-search${testid ? '?testid=' + testid : ''}`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        data: input,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return result.data;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Timed out while fetching search result');
      }
      throw error;
    }
  });
};

const uploadFile = async (file, meta) => {
  const fileType = file.type;
  const filename = file.name;

  meta.filetype = fileType;
  meta.filename = filename;

  return makeAuthenticatedRequest(async (token) => {
    // Convert file to ArrayBuffer
    const data = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsArrayBuffer(file);
    });

    try {
      // Get s3 signed upload URL
      const options = {
        method: 'GET',
        url: `${apiBaseUrl}/get-upload-url?strm=${meta.strm}&classNumber=${meta.classNumber}`,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': fileType,
        },
        data: data,
      };

      const result = await axios(options);
      const uploadUrl = result.data.uploadURL;

      // Upload to S3
      const s3Put = {
        method: 'PUT',
        url: uploadUrl,
        headers: {
          'Content-Type': fileType,
        },
        data: data,
      };

      await axios(s3Put);

      const publishStatus = await pollStatus(meta, 1000);

      if (publishStatus === 'READY') {
        return `Syllabus for class ${meta.classNumber} uploaded successfully!`;
      } else {
        throw new Error(
          `An error occurred while processing the syllabus file for class ${meta.classNumber}. Please try again or check back later.`
        );
      }
    } catch (error) {
      throw new Error(
        `Failed to upload syllabus for class ${meta.classNumber}: ${error.message}`
      );
    }
  });
};

const uploadUrl = async (url, meta) => {
  meta.url = url;

  return makeAuthenticatedRequest(async (token) => {
    const options = {
      method: 'POST',
      url: `${apiBaseUrl}/upload-from-url?strm=${meta.strm}&classNumber=${meta.classNumber}`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      data: meta,
    };

    const result = await axios(options);
    return 'Syllabus updated successfully!';
  });
};

const getTerms = async () => {
  const now = new Date();
  const halfRange = 31540000 * 1000; // 12 months
  const pastLimit = new Date(now.getTime() - halfRange).getTime();
  const futureLimit = new Date(now.getTime() + halfRange).getTime();

  const strmsAll = await axios({
    method: 'GET',
    url: `${dplBaseUrl}/codeset/terms`,
  });

  const strmsArr = Object.values(strmsAll.data);
  let strms = strmsArr.filter(
    (strm) =>
      strm.latestTermEndDate >= pastLimit &&
      strm.earliestTermBeginDate <= futureLimit
  );

  let strmsSimple = strms.map((strm) => ({
    strm: strm.strm,
    description: strm.description,
  }));
  strmsSimple.sort((a, b) => (a.strm > b.strm ? -1 : 1)); // Descending order

  const current = await axios({
    method: 'GET',
    url: `${dplBaseUrl}/codeset/term/current`,
  });

  return {
    termsList: strmsSimple,
    workingTerm: current.data.strm,
  };
};

const syllabiExist = async (classes) => {
  return makeAuthenticatedRequest(async (token) => {
    const result = await axios({
      method: 'POST',
      url: `${apiBaseUrl}/syllabi-exist`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      data: classes,
    });
    return result.data;
  });
};

const pollStatus = (params, interval, maxTries) => {
  let attempts = 0;

  return new Promise((resolve, reject) => {
    const executePoll = async () => {
      try {
        const result = await getPublishStatus(params);
        attempts++;

        if (result === 'READY') {
          resolve(result);
        } else if (result === 'ERROR') {
          reject(result);
        } else if (maxTries && maxTries === attempts) {
          reject('Timed out');
        } else {
          setTimeout(executePoll, interval);
        }
      } catch (error) {
        reject(error);
      }
    };

    executePoll();
  });
};

const getPublishStatus = async ({ strm, classNumber }) => {
  return makeAuthenticatedRequest(async (token) => {
    const statusOptions = {
      method: 'GET',
      url: `${apiBaseUrl}/get-publish-status?strm=${strm}&classNumber=${classNumber}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    const publishStatusResp = await axios(statusOptions);
    return publishStatusResp.data.status;
  });
};

export {
  search,
  getDownloadUrl,
  uploadFile,
  uploadUrl,
  getUsers,
  addUser,
  removeUsers,
  getUserDetail,
  getClassList,
  getEdnaAccess,
  getEdnaAccessList,
  removeSyllabus,
  getTerms,
  syllabiExist,
};
